import { Component, OnInit, HostBinding, Output, EventEmitter, Input, ElementRef, AfterViewInit, OnChanges } from '@angular/core';

@Component({
  selector: 'app-button',
  templateUrl: './app-button.component.html',
  styleUrls: ['./app-button.component.scss'],
  standalone: false,
})
export class AppButtonComponent implements OnInit, AfterViewInit, OnChanges {
  private _color: string = 'primary';
  private _textColor: string = 'primary';
  private _textColorProvided: boolean = false;
  private _height: string = '15vw';
  private _width: string = '100%';
  private _borderRad: string = '15px';
  private _margin: string = '0px';
  private _padding: string = '0px';
  private _opacity: number[] = [8, 7, 8];
  private _colorMod: string = 'color';
  private _textMod: string = 'text';

  @Input() disabled: boolean = false;

  @Input()
  set color(value: string | undefined) {
    this._color = value || 'primary';
    this.updateColorVariables();
  }

  @Input()
  set textColor(value: string | undefined) {
    this._textColorProvided = !!value;
    if (value === 'white' || value === '#fff' || value === '#ffffff') {
      this._textColor = 'white';
      this._textMod = 'color';
    } else if (value) {
      this._textColor = value;
      this._textMod = 'text';
    }
    this.updateColorVariables();
  }

  @Input()
  set colorMod(value: string | undefined) {
    this._colorMod = value || 'color';
    this.updateColorVariables();
  }

  @Input()
  set textMod(value: string | undefined) {
    this._textMod = value || 'text';
    this.updateColorVariables();
  }

  @Input()
  set height(value: string | number | undefined) {
    this._height = this.normalizeUnit(value, '15vw');
    this.updateDimensionVariables();
  }

  @Input()
  set width(value: string | number | undefined) {
    this._width = this.normalizeUnit(value, '100%');
    this.updateDimensionVariables();
  }

  @Input()
  set borderRad(value: string | number | undefined) {
    this._borderRad = this.normalizeUnit(value, '15px');
    this.updateBorderRadius();
  }

  @Input()
  set margin(value: string | number | undefined) {
    this._margin = this.normalizeSpacing(value, '0px');
    this.updateSpacing();
  }

  @Input()
  set padding(value: string | number | undefined) {
    this._padding = this.normalizeSpacing(value, '0px');
    this.updateSpacing();
  }

  @Input()
  set opacity(value: number[] | undefined) {
    this._opacity = value || [8, 7, 8];
    this.updateOpacityVariables();
  }

  @HostBinding('class.is-hovered')
  isHovered: boolean = false;

  @HostBinding('class.is-active')
  isActive: boolean = false;

  @Output()
  buttonClick = new EventEmitter<void>();

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    this.updateColorVariables();
    this.updateDimensionVariables();
    this.updateBorderRadius();
    this.updateOpacityVariables();
  }

  ngAfterViewInit(): void {
    this.updateColorVariables();
    this.updateDimensionVariables();
    this.updateBorderRadius();
    this.updateOpacityVariables();
  }

  ngOnChanges():void{
        this.updateColorVariables();
    this.updateDimensionVariables();
    this.updateBorderRadius();
    this.updateOpacityVariables();
  }

  private normalizeUnit(value: string | number | undefined, defaultValue: string): string {
    if (!value && value !== 0) return defaultValue;
    const strValue = String(value).trim();
    const hasUnit = /[a-zA-Z%]/.test(strValue);
    if (hasUnit) return strValue;
    return `${strValue}vw`;
  }

  private normalizeSpacing(value: string | number | undefined, defaultValue: string): string {
    if (!value && value !== 0) return defaultValue;
    const strValue = String(value).trim();
    const values = strValue.split(/\s+/);
    
    return values.map(val => {
      const hasUnit = /[a-zA-Z%]/.test(val);
      if (hasUnit) return val;
      return `${val}px`;
    }).join(' ');
  }

  private updateColorVariables(): void {
    const colorVar = `var(--ion-${this._colorMod}-${this._color})`;
    let textColorVar: string;
    if (this._textColor === 'white') {
      textColorVar = '#ffffff';
    } else if (this._textColorProvided) {
      textColorVar = `var(--ion-${this._textMod}-${this._textColor})`;
    } else {
      textColorVar = `var(--ion-${this._textMod}-${this._color})`;
    }
    this.el.nativeElement.style.setProperty('--button-color', colorVar);
    this.el.nativeElement.style.setProperty('--text-color', textColorVar);
  }

  private updateDimensionVariables(): void {
    this.el.nativeElement.style.setProperty('--height', this._height);
    this.el.nativeElement.style.setProperty('--width', this._width);
  }

  private updateBorderRadius(): void {
    this.el.nativeElement.style.setProperty('--border-round', this._borderRad);
  }

  private updateSpacing(): void {
    this.el.nativeElement.style.setProperty('--margin', this._margin);
    this.el.nativeElement.style.setProperty('--padding', this._padding);
  }

  private updateOpacityVariables(): void {
    const opacityMap = [
      { index: 0, varName: '--clear-color' },
      { index: 1, varName: '--clear2-color' },
      { index: 2, varName: '--clear3-color' },
    ];

    opacityMap.forEach(({ index, varName }) => {
      const opacityValue = this._opacity[index] ?? 8;
      const clampedOpacity = Math.max(0, Math.min(10, opacityValue));
      const opacityDecimal = clampedOpacity / 10;
      const colorValue = `hsl(var(--drop-color-${this._color}) / ${opacityDecimal})`;
      this.el.nativeElement.style.setProperty(varName, colorValue);
    });
  }

  onPointerEnter(): void {
    if (!this.disabled) {
      this.isHovered = true;
      this.isActive = false;
    }
  }

  onPointerLeave(): void {
    if (!this.disabled) {
      this.isHovered = false;
      this.isActive = false;
    }
  }

  onPointerDown(): void {
    if (!this.disabled) {
      this.isActive = false;
      this.isHovered = true;
    }
  }

  onPointerUp(): void {
    if (!this.disabled) {
      this.isActive = true;
      this.isHovered = false;
    }
  }

  onClick(): void {
    if (!this.disabled) {
      this.buttonClick.emit();
    }
    this.isActive = false;
    this.isHovered = false;
  }
}
